import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, faMotorcycle, faUser, faCheckCircle, 
  faArrowRight, faCar
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from './Auth/AuthContext';
import { DRIVER_STATUSES, ROLES } from '../../constants';
import { getRoleIdByName, assignRoleToUser } from '../../services/api/trips-ui';
import { completeOnboardingProfile } from '../../services/api/account';
import { upsertDriverProfile } from '../DELIVERY/services/deliveryServices';
import Swal from 'sweetalert2';

/**
 * OnboardingModal — DoorDash-inspired welcome flow.
 * Clean, professional, and emoji-free.
 */
const OnboardingModal = () => {
  const navigate = useNavigate();
  const { currentUser, profile, isAdmin, isSuperAdmin } = useAuth();
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState(() => {
    const savedRole = localStorage.getItem('pending_oauth_role');
    if (savedRole) {
      localStorage.removeItem('pending_oauth_role');
      return savedRole;
    }
    return null;
  });
  const [phone, setPhone] = useState('');
  const [plate, setPlate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser || !profile) return;
    // Admins NUNCA pasan onboarding: el flujo asume rol base
    // (pasajero/conductor) y completeOnboardingProfile escribiria
    // role='passenger' en profiles — degradaria una cuenta admin
    // solo por completar el modal.
    if (isAdmin || isSuperAdmin) return;
    const DONE_KEY = `onboarding_done_${currentUser.id}`;
    if (localStorage.getItem(DONE_KEY)) return;

    const needsOnboarding = !profile.phone || !profile.onboarding_complete;
    if (needsOnboarding) {
      const t = setTimeout(() => setShow(true), 1200);
      return () => clearTimeout(t);
    }
  }, [currentUser, profile, isAdmin, isSuperAdmin]);

  const displayName =
    profile?.name ||
    currentUser?.user_metadata?.full_name ||
    currentUser?.user_metadata?.name ||
    currentUser?.user_metadata?.display_name ||
    'Usuario';

  const isDriver = selectedRole === ROLES.DRIVER;
  const STEPS_TOTAL = isDriver ? 3 : 2;

  const handleSkip = () => {
    if (currentUser) {
      localStorage.setItem(`onboarding_done_${currentUser.id}`, '1');
    }
    setShow(false);
  };

  const handleNext = () => {
    if (step === 1 && !selectedRole) {
      Swal.fire('Selección requerida', 'Por favor, elige tu rol para continuar.', 'warning');
      return;
    }
    setStep(s => s + 1);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Actualizar perfil base (nombre, teléfono, rol, onboarding completo)
      await completeOnboardingProfile(currentUser.id, {
        name: displayName,
        phone: phone.trim() || null,
        role: isDriver ? ROLES.DRIVER : ROLES.PASSENGER,
      });

      // 2. Si es conductor: crear registro en tabla 'drivers' y sincronizar rol inactivo
      if (isDriver) {
        try {
          await upsertDriverProfile({
            driver_uid: currentUser.id,
            name: displayName,
            phone: phone.trim() || null,
            plate: plate.trim().toUpperCase() || 'PENDIENTE',
            vehicle_type: 'moto',
            status: DRIVER_STATUSES.PENDING,
            is_approved: false,
          });

          const { data: driverRole } = await getRoleIdByName(ROLES.DRIVER);

          if (driverRole?.id) {
            await assignRoleToUser(currentUser.id, driverRole.id, currentUser.id);
          }
        } catch (driverErr) {
          // No-bloqueante: el perfil ya se guardó, el admin puede completar el registro
          console.warn('Driver profile creation warning:', driverErr.message);
        }
      }

      localStorage.setItem(`onboarding_done_${currentUser.id}`, '1');
      setShow(false);

      Swal.fire({
        icon: 'info',
        title: `¡Hola, ${displayName.split(' ')[0]}!`,
        html: isDriver
          ? 'Tu registro como conductor ha sido recibido con éxito.<br><br>Para activarte y comenzar a recibir viajes, sube tus documentos reglamentarios (Cédula, Licencia, Certificación INTT, Título/Carnet de Circulación y RIF) directamente en el <b>Centro de Documentación</b> de la app.'
          : '¡Bienvenido! Ya puedes solicitar tu primer servicio.',
        confirmButtonColor: 'var(--bry-accent)',
        confirmButtonText: isDriver ? 'Subir Documentos' : 'Entendido',
        showCancelButton: isDriver,
        cancelButtonText: 'Más tarde',
      }).then((result) => {
        if (isDriver && result.isConfirmed) {
          navigate('/documentos');
        }
      });
    } catch (err) {
      console.error('Error saving profile:', err);
      Swal.fire('Error', 'No se pudo guardar tu perfil. Intenta de nuevo.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={handleSkip} centered backdrop="static" size="sm" className="ob-modal">
      <Modal.Body className="p-0" style={{ borderRadius: 20, overflow: 'hidden' }}>
        
        {/* Header Section */}
        <div className="ob-header" style={{
          background: 'linear-gradient(135deg, var(--bry-cyan) 0%, var(--bry-navy) 100%)',
          padding: '28px 24px 20px',
          textAlign: 'center',
          color: 'var(--bry-text-main)'
        }}>
          <motion.div
            key={step}
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            style={{
              width: 50, height: 50, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px',
            }}
          >
            {step === 1 && <FontAwesomeIcon icon={faUser} size="lg" />}
            {step === 2 && <FontAwesomeIcon icon={faPhone} size="lg" />}
            {step === 3 && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <FontAwesomeIcon icon={faMotorcycle} size="lg" />
                <FontAwesomeIcon icon={faCar} size="lg" />
              </div>
            )}
          </motion.div>
          <h5 style={{ fontWeight: 800, marginBottom: 4, fontSize: 18 }}>
            {step === 1 && '¡Bienvenido!'}
            {step === 2 && 'Casi listo'}
            {step === 3 && 'Tu vehículo'}
          </h5>
          <p style={{ opacity: 0.9, fontSize: 13, marginBottom: 0 }}>
            {step === 1 && `Hola, ${displayName.split(' ')[0]}. ¿Cómo usarás la app?`}
            {step === 2 && 'Ingresa tu número de contacto.'}
            {step === 3 && 'Registra tu vehículo para operar.'}
          </p>
        </div>

        {/* Progress Bar */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '12px 0 4px' }}>
          {Array.from({ length: STEPS_TOTAL }).map((_, i) => (
            <div key={i} style={{
              width: 24, height: 4, borderRadius: 2,
              background: i + 1 <= step ? 'var(--bry-accent)' : '#EEE',
              transition: 'background 0.3s',
            }} />
          ))}
        </div>

        {/* Form Section */}
        <div style={{ padding: '20px 24px 24px' }}>
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                <button
                  onClick={() => setSelectedRole(ROLES.PASSENGER)}
                  style={{
                    border: `2px solid ${selectedRole === ROLES.PASSENGER ? 'var(--bry-cyan)' : '#F0F0F0'}`,
                    borderRadius: 16, padding: '16px',
                    background: selectedRole === ROLES.PASSENGER ? 'rgba(14, 171, 176, 0.08)' : '#FFF',
                    display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', width: '100%'
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', 
                    background: selectedRole === ROLES.PASSENGER ? 'var(--bry-accent)' : '#F5F5F5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <FontAwesomeIcon icon={faUser} color={selectedRole === ROLES.PASSENGER ? '#FFF' : '#AAA'} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>Pasajero</div>
                    <div style={{ fontSize: 12, color: '#666' }}>Quiero pedir viajes o comida</div>
                  </div>
                  {selectedRole === ROLES.PASSENGER && <FontAwesomeIcon icon={faCheckCircle} color="var(--bry-cyan)" />}
                </button>

                <button
                  onClick={() => setSelectedRole(ROLES.DRIVER)}
                  style={{
                    border: `2px solid ${selectedRole === ROLES.DRIVER ? 'var(--bry-cyan)' : '#F0F0F0'}`,
                    borderRadius: 16, padding: '16px',
                    background: selectedRole === ROLES.DRIVER ? 'rgba(14, 171, 176, 0.08)' : '#FFF',
                    display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', width: '100%'
                  }}
                >
                  <div style={{
                    minWidth: 50, height: 40, borderRadius: '20px', 
                    background: selectedRole === ROLES.DRIVER ? 'var(--bry-accent)' : '#F5F5F5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, gap: '4px', padding: '0 8px'
                  }}>
                    <FontAwesomeIcon icon={faMotorcycle} color={selectedRole === ROLES.DRIVER ? '#FFF' : '#AAA'} style={{ fontSize: '14px' }} />
                    <FontAwesomeIcon icon={faCar} color={selectedRole === ROLES.DRIVER ? '#FFF' : '#AAA'} style={{ fontSize: '14px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>Conductor</div>
                    <div style={{ fontSize: 12, color: '#666' }}>Quiero trabajar con mi vehículo</div>
                  </div>
                  {selectedRole === ROLES.DRIVER && <FontAwesomeIcon icon={faCheckCircle} color="var(--bry-cyan)" />}
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="s2"
                initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
              >
                <Form.Group>
                  <Form.Label style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', color: '#888' }}>
                    Teléfono de Contacto
                  </Form.Label>
                  <Form.Control
                    type="tel" placeholder="0414-0000000" value={phone}
                    onChange={e => setPhone(e.target.value)}
                    style={{ borderRadius: 12, border: '1px solid #DDD', padding: '14px', fontSize: 16 }}
                    autoFocus
                  />
                </Form.Group>
              </motion.div>
            )}

            {step === 3 && isDriver && (
              <motion.div
                key="s3"
                initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
              >
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', color: '#888' }}>
                    Número de Placa
                  </Form.Label>
                  <Form.Control
                    type="text" placeholder="ABC-123" value={plate}
                    onChange={e => setPlate(e.target.value)}
                    style={{ borderRadius: 12, border: '1px solid #DDD', padding: '14px', fontSize: 16, textTransform: 'uppercase' }}
                    autoFocus
                  />
                </Form.Group>
                <div style={{ 
                  background: 'rgba(51, 51, 51, 0.08)', padding: '12px', borderRadius: 12, 
                  fontSize: 12, color: 'var(--bry-navy)', border: '1px solid rgba(51, 51, 51, 0.2)' 
                }}>
                  <strong>Importante:</strong> Debes validar tus documentos en nuestra sede física para activar tu cuenta.
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 28 }}>
            <Button
              variant="light"
              style={{ flex: 1, borderRadius: 12, fontWeight: 700, fontSize: 14, background: '#F5F5F5', border: 'none' }}
              onClick={step === 1 ? handleSkip : () => setStep(s => s - 1)}
            >
              {step === 1 ? 'Omitir' : 'Atrás'}
            </Button>

            {step < STEPS_TOTAL ? (
              <Button
                variant="primary"
                style={{ flex: 1, borderRadius: 12, fontWeight: 700, fontSize: 14, background: 'var(--bry-cyan)', border: 'none' }}
                onClick={handleNext}
                disabled={step === 1 && !selectedRole}
              >
                Siguiente <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 6 }} />
              </Button>
            ) : (
              <Button
                variant="primary"
                style={{ flex: 1, borderRadius: 12, fontWeight: 700, fontSize: 14, background: 'var(--bry-cyan)', border: 'none' }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <Spinner size="sm" /> : <>Finalizar <FontAwesomeIcon icon={faCheckCircle} style={{ marginLeft: 6 }} /></>}
              </Button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default OnboardingModal;
